import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Plus, X, Loader2, CheckCircle2, Save, Edit3, Trash2,
  Receipt, DollarSign, TrendingDown, Calendar, FileText,
  Zap, Home, Users, Wrench, Megaphone, MoreHorizontal,
  Filter, ArrowUpDown, Truck, Coffee, Package,
  Droplets, Wifi, Building2, Car, Box
} from 'lucide-react';
import { Haptics } from '../../utils/haptics';
import { confirmAction } from './ConfirmModal';

interface Expense {
  id: number;
  category: string;
  amount: number;
  description: string | null;
  expenseDate: string;
  createdAt: string;
}

interface ExpenseManagementProps {
  storeApiKey: string;
  isReadOnly?: boolean;
}

const CATEGORIES = ['Electricity Bill', 'Water Bill', 'Telephone/Internet', 'Rent', 'Payroll', 'Transport & Fuel', 'Staff Meals & Tea', 'Packaging Materials', 'Maintenance', 'Marketing', 'Other'] as const;

const CATEGORY_META: Record<string, { icon: any; gradient: string; lightBg: string }> = {
  'Electricity Bill':  { icon: Zap,         gradient: 'from-amber-400 to-orange-500', lightBg: 'bg-amber-50 border-amber-200' },
  'Water Bill':        { icon: Droplets,    gradient: 'from-sky-400 to-blue-500', lightBg: 'bg-sky-50 border-sky-200' },
  'Telephone/Internet':{ icon: Wifi,        gradient: 'from-violet-400 to-purple-500', lightBg: 'bg-violet-50 border-violet-200' },
  'Rent':              { icon: Building2,   gradient: 'from-rose-400 to-red-500', lightBg: 'bg-rose-50 border-rose-200' },
  'Payroll':           { icon: Users,       gradient: 'from-indigo-400 to-blue-600', lightBg: 'bg-indigo-50 border-indigo-200' },
  'Transport & Fuel':  { icon: Car,         gradient: 'from-slate-600 to-slate-800', lightBg: 'bg-slate-100 border-slate-300' },
  'Staff Meals & Tea': { icon: Coffee,      gradient: 'from-orange-400 to-red-500', lightBg: 'bg-orange-50 border-orange-200' },
  'Packaging Materials':{ icon: Box,        gradient: 'from-emerald-400 to-teal-500', lightBg: 'bg-emerald-50 border-emerald-200' },
  'Maintenance':       { icon: Wrench,      gradient: 'from-zinc-500 to-slate-700', lightBg: 'bg-zinc-50 border-zinc-200' },
  'Marketing':         { icon: Megaphone,   gradient: 'from-fuchsia-400 to-pink-500', lightBg: 'bg-fuchsia-50 border-fuchsia-200' },
  'Other':             { icon: Receipt,     gradient: 'from-slate-400 to-slate-600', lightBg: 'bg-slate-50 border-slate-200' }
};

const ExpenseManagement: React.FC<ExpenseManagementProps> = ({ storeApiKey, isReadOnly = false }) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('All');

  const [form, setForm] = useState({
    category: 'Other',
    amount: '',
    description: '',
    expenseDate: new Date().toISOString().slice(0, 10),
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/expenses', {
        headers: { 'x-store-api-key': storeApiKey },
      });
      const data = await res.json();
      if (res.ok) setExpenses(data.expenses || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchExpenses(); }, []);

  const resetForm = () => {
    setForm({ category: 'Other', amount: '', description: '', expenseDate: new Date().toISOString().slice(0, 10) });
    setFormError('');
  };

  const createExpense = async () => {
    setFormError('');
    if (!form.amount || Number(form.amount) <= 0) { setFormError('Valid amount is required'); Haptics.fire('warn'); return; }
    if (!form.expenseDate) { setFormError('Date is required'); Haptics.fire('warn'); return; }
    setSaving(true);
    try {
      const res = await fetch('/api/expenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey },
        body: JSON.stringify({
          category: form.category,
          amount: Number(form.amount),
          description: form.description.trim() || null,
          expenseDate: form.expenseDate,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.error || 'Failed to create'); Haptics.fire('warn'); return; }
      Haptics.fire('success');
      setCreateOpen(false);
      resetForm();
      fetchExpenses();
    } catch { setFormError('Network error'); Haptics.fire('warn'); }
    finally { setSaving(false); }
  };

  const updateExpense = async () => {
    if (!editing) return;
    setFormError('');
    setSaving(true);
    try {
      const body: any = {};
      if (form.category !== undefined) body.category = form.category;
      if (form.amount !== undefined) body.amount = Number(form.amount);
      if (form.description !== undefined) body.description = form.description.trim() || null;
      if (form.expenseDate !== undefined) body.expenseDate = form.expenseDate;
      const res = await fetch(`/api/expenses/${editing.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.error || 'Failed to update'); Haptics.fire('warn'); return; }
      Haptics.fire('success');
      setEditOpen(false);
      setEditing(null);
      resetForm();
      fetchExpenses();
    } catch { setFormError('Network error'); Haptics.fire('warn'); }
    finally { setSaving(false); }
  };

  const deleteExpense = async (e: Expense) => {
    const ok = await confirmAction({
      title: `Delete this expense?`,
      message: `${e.category} expense of Rs. ${Number(e.amount).toLocaleString()} on ${e.expenseDate} will be permanently removed.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await fetch(`/api/expenses/${e.id}`, {
        method: 'DELETE',
        headers: { 'x-store-api-key': storeApiKey },
      });
      if (res.ok) { Haptics.fire('warn'); fetchExpenses(); }
    } catch { /* silent */ }
  };

  const beginEdit = (e: Expense) => {
    setEditing(e);
    setForm({ category: e.category, amount: String(e.amount), description: e.description || '', expenseDate: e.expenseDate });
    setFormError('');
    setEditOpen(true);
  };

  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const monthlyTotal = useMemo(() =>
    expenses
      .filter(e => e.expenseDate >= monthStart)
      .reduce((s, e) => s + Number(e.amount), 0),
    [expenses, monthStart]
  );

  const filtered = categoryFilter === 'All'
    ? expenses
    : expenses.filter(e => e.category === categoryFilter);

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-200 flex items-center justify-center">
            <Receipt size={15} className="text-white" />
          </div>
          <div>
            <h2 className="text-[15px] font-black text-slate-800">Expenses & Bills</h2>
            <p className="text-[10px] text-slate-500">Business overhead tracking</p>
          </div>
        </div>
        <motion.button whileTap={{ scale: 0.96 }}
          onClick={() => { setCreateOpen(true); resetForm(); }}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-600 text-white text-[11px] font-black uppercase tracking-wider shadow-lg shadow-amber-500/20">
          <Plus size={13} /> Add Expense
        </motion.button>
      </div>

      {/* KPI Card — Monthly Total */}
      <div className="shrink-0 px-5 py-3 border-b border-slate-100">
        <div className="flex items-center gap-4 px-4 py-3 rounded-2xl bg-white/70 backdrop-blur-sm border border-slate-200 shadow-sm">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-md shrink-0">
            <TrendingDown size={18} />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Total Expenses This Month</p>
            <p className="text-[20px] font-black text-slate-800 font-mono tracking-tight">
              Rs. {monthlyTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="shrink-0 flex items-center gap-2 px-5 py-2 border-b border-slate-100 bg-white/40 backdrop-blur-sm overflow-x-auto">
        <button key="All" onClick={() => setCategoryFilter('All')}
          className={`shrink-0 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
            categoryFilter === 'All'
              ? 'bg-amber-100 text-amber-700 border border-amber-200 shadow-sm'
              : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
          }`}>
          <Filter size={10} className="inline mr-1" /> All
        </button>
        {CATEGORIES.map(c => {
          const Icon = CATEGORY_META[c].icon;
          return (
            <button key={c} onClick={() => setCategoryFilter(c)}
              className={`shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
                categoryFilter === c
                  ? `${CATEGORY_META[c].lightBg} shadow-sm`
                  : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
              }`}>
              <Icon size={10} /> {c}
            </button>
          );
        })}
        <div className="flex-1 text-right text-[10px] text-slate-400 font-mono">{filtered.length} expense{filtered.length !== 1 ? 's' : ''}</div>
      </div>

      {/* Expense list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading && (
          <div className="flex items-center justify-center py-12 text-slate-400 text-[11px]">
            <Loader2 size={14} className="animate-spin mr-2" /> Loading…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 text-slate-400 text-[11px]">
            <Receipt size={28} className="mx-auto mb-2 text-slate-300" />
            {categoryFilter !== 'All' ? 'No expenses match this filter.' : 'No expenses recorded yet.'}
          </div>
        )}
        {filtered.map((e) => {
          const meta = CATEGORY_META[e.category] || CATEGORY_META.Other;
          const Icon = meta.icon;
          return (
            <motion.div key={e.id} layout
              className="rounded-2xl border border-slate-200 p-3 bg-white/70 backdrop-blur-sm transition-all hover:shadow-sm">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${meta.gradient} flex items-center justify-center text-white shrink-0 shadow-sm`}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-black text-slate-800">{e.category}</span>
                    <span className="text-[12px] font-black font-mono text-rose-600">
                      -Rs. {Number(e.amount).toLocaleString()}
                    </span>
                  </div>
                  {e.description && (
                    <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                      <FileText size={9} className="shrink-0 text-slate-400" />
                      <span className="truncate">{e.description}</span>
                    </div>
                  )}
                  <div className="text-[9px] text-slate-400 font-mono flex items-center gap-2 mt-0.5">
                    <Calendar size={8} /> {e.expenseDate}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => beginEdit(e)}
                    className="w-8 h-8 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 transition-colors">
                    <Edit3 size={12} />
                  </button>
                  <button onClick={() => deleteExpense(e)}
                    className="w-8 h-8 rounded-lg bg-rose-50 hover:bg-rose-100 border border-rose-200 flex items-center justify-center text-rose-500 transition-colors">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {createOpen && (
          <Modal onClose={() => setCreateOpen(false)} title="Add Expense">
            <div className="space-y-3 p-1">
              <Field label="Category" icon={Filter}>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-amber-400 text-[13px]">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Amount (Rs.)" icon={DollarSign}>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[13px] font-mono">Rs.</span>
                  <input type="number" min={0} step={10} value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-amber-400 text-[13px] font-mono" />
                </div>
              </Field>
              <Field label="Date" icon={Calendar}>
                <input type="date" value={form.expenseDate}
                  onChange={e => setForm(f => ({ ...f, expenseDate: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-amber-400 text-[13px]" />
              </Field>
              <Field label="Description (optional)" icon={FileText}>
                <textarea value={form.description} rows={2}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Brief note about this expense"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-amber-400 text-[13px] resize-none" />
              </Field>
              {formError && (
                <div className="text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{formError}</div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button onClick={() => setCreateOpen(false)}
                  className="flex-1 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-black uppercase">
                  Cancel
                </button>
                <motion.button whileTap={{ scale: 0.97 }} onClick={createExpense} disabled={saving}
                  className="flex-1 py-2.5 rounded-xl bg-amber-600 text-white text-[11px] font-black uppercase shadow-lg shadow-amber-500/20 disabled:opacity-50 flex items-center justify-center gap-1">
                  {saving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                  Add
                </motion.button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {editOpen && editing && (
          <Modal onClose={() => setEditOpen(false)} title="Edit Expense">
            <div className="space-y-3 p-1">
              <Field label="Category" icon={Filter}>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-amber-400 text-[13px]">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Amount (Rs.)" icon={DollarSign}>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono">Rs.</span>
                  <input type="number" min={0} step={10} value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-amber-400 text-[13px] font-mono" />
                </div>
              </Field>
              <Field label="Date" icon={Calendar}>
                <input type="date" value={form.expenseDate}
                  onChange={e => setForm(f => ({ ...f, expenseDate: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-amber-400 text-[13px]" />
              </Field>
              <Field label="Description" icon={FileText}>
                <textarea value={form.description} rows={2}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-amber-400 text-[13px] resize-none" />
              </Field>
              {formError && (
                <div className="text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{formError}</div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button onClick={() => setEditOpen(false)}
                  className="flex-1 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-black uppercase">
                  Cancel
                </button>
                <motion.button whileTap={{ scale: 0.97 }} onClick={updateExpense} disabled={saving}
                  className="flex-1 py-2.5 rounded-xl bg-amber-600 text-white text-[11px] font-black uppercase shadow-lg shadow-amber-500/20 disabled:opacity-50 flex items-center justify-center gap-1">
                  {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                  Save
                </motion.button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

const Field: React.FC<{ label: string; icon: any; children: React.ReactNode }> = ({ label, icon: Icon, children }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
      <Icon size={10} /> {label}
    </label>
    {children}
  </div>
);

const Modal: React.FC<{ onClose: () => void; title: string; children: React.ReactNode }> = ({ onClose, title, children }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[9998] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <motion.div initial={{ y: 400 }} animate={{ y: 0 }} exit={{ y: 400 }}
      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
      className="w-full sm:max-w-md max-h-[92vh] bg-white/90 backdrop-blur-2xl rounded-t-3xl sm:rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col">
      <div className="shrink-0 px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-[14px] font-black text-slate-800">{title}</h3>
        <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700">
          <X size={15} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">{children}</div>
    </motion.div>
  </motion.div>
);

export default ExpenseManagement;
